import { useEffect, useState } from "react";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { eur, setScrollLock, imgSrc } from "@/lib/api";
import { useCart } from "@/lib/cart";

export const ProductModal = ({ product, onClose }) => {
  const { add, setOpen } = useCart();
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setQty(1);
    setScrollLock(!!product);
    return () => setScrollLock(false);
  }, [product]);

  if (!product) return null;
  const specs = Object.entries(product.specs || {});
  const addNow = () => {
    add(product, qty);
    toast.success(`${product.name} aggiunto al carrello`);
    onClose();
    setOpen(true);
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent data-testid="product-modal"
        className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-[2rem] border-fg/10 bg-[var(--ink-2)] p-0 sm:rounded-[2rem]">
        <div className="grid md:grid-cols-2">
          <div className="aspect-square overflow-hidden bg-black md:aspect-auto">
            <img src={imgSrc(product.image)} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <div className="flex flex-col p-7">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[var(--volt)]">{product.categoryLabel || product.category}</p>
            <DialogTitle className="mt-2 font-display text-4xl font-semibold">{product.name}</DialogTitle>
            <DialogDescription className="mt-2 text-fg2">{product.tagline}</DialogDescription>
            <dl className="mt-6 divide-y divide-fg/10 border-y border-fg/10">
              {specs.map(([k, v]) => (
                <div key={k} className="flex justify-between py-2.5 text-sm">
                  <dt className="text-mist">{k}</dt><dd className="text-fg">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs text-mist" data-testid="product-modal-stock">Disponibilità: {product.stock} pezzi</p>
            <div className="mt-auto flex items-center justify-between gap-4 pt-6">
              <span className="font-display text-3xl font-semibold">{eur(product.price * qty)}</span>
              <div className="flex items-center gap-1 rounded-full border border-fg/10 p-1">
                <button data-testid="product-modal-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-fg/10"><Minus className="h-4 w-4" /></button>
                <span className="w-6 text-center" data-testid="product-modal-qty">{qty}</span>
                <button data-testid="product-modal-qty-plus" onClick={() => setQty((q) => Math.min(10, q + 1))} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-fg/10"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
            <button onClick={addNow} data-testid="product-modal-add-button"
              className="shine mt-5 flex items-center justify-center gap-2 rounded-full bg-[var(--volt)] py-4 font-semibold text-[var(--ink)] active:scale-95">
              <ShoppingBag className="h-5 w-5" /> Aggiungi al carrello
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
